import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

export const ExternalGetUserResponseSchema = z.object({
  _id: z.string(),
  email: z.string(),
  phoneNumber: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

export type ExternalGetUserResponse = z.infer<typeof ExternalGetUserResponseSchema>;

export const ExternalGetTemplatesResponseSchema = z.object({
  _id: z.string(),
  name: z.string(),
  type: z.string(),
});

export type ExternalGetTemplatesResponse = z.infer<typeof ExternalGetTemplatesResponseSchema>;

export const ExternalGetCustomersResponseSchema = z.object({
  _id: z.string(),
  name: z.string(),
});

export type ExternalGetCustomersResponse = z.infer<typeof ExternalGetCustomersResponseSchema>;

// passthrough, not strict: unknown filters must reach the service, which rejects them with the
// partner-facing `<key> field is invalid!` message rather than a ts-rest schema error body.
export const ExternalGetBatchesBodySchema = z
  .object({
    batch: z.string().optional(),
    name: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    isReleased: z.string().optional(),
  })
  .passthrough();

export type ExternalGetBatchesBody = z.infer<typeof ExternalGetBatchesBodySchema>;

export const ExternalGetBatchesResponseSchema = z.object({
  _id: z.any(),
  createdAt: z.union([z.string(), z.date()]).nullable().optional(),
  customer: z.any(),
  declarant: z.any(),
  deleted: z.boolean().nullable().optional(),
  name: z.string().nullable().optional(),
  status: z
    .object({
      details: z.string().nullable().optional(),
      value: z.string().nullable().optional(),
    })
    .optional(),
  filename: z.string().nullable().optional(),
  statistics: z
    .object({
      accepted: z.number().nullable().optional(),
      inControl: z.number().nullable().optional(),
      invalidated: z.number().nullable().optional(),
      notReleased: z.number().nullable().optional(),
      rejected: z.number().nullable().optional(),
      released: z.number().nullable().optional(),
      preAccepted: z.number().nullable().optional(),
      preInControl: z.number().nullable().optional(),
      preSent: z.number().nullable().optional(),
      sent: z.number().nullable().optional(),
      total: z.number().nullable().optional(),
    })
    .optional(),
});

export type ExternalGetBatchesResponse = z.infer<typeof ExternalGetBatchesResponseSchema>;

export const ExternalGetDeclarationsBodySchema = z.object({
  batch: z.string().optional(),
  LRN: z.string().optional(),
  referenceNumberUCR: z.string().optional(),
});

export type ExternalGetDeclarationsBody = z.infer<typeof ExternalGetDeclarationsBodySchema>;

export const ExternalGetAuditTrailBodySchema = z
  .object({
    batch: z.string().optional(),
    LRN: z.string().optional(),
  })
  .refine((body) => !!body.batch !== !!body.LRN, { message: 'Provide exactly one of batch or LRN' });

export type ExternalGetAuditTrailBody = z.infer<typeof ExternalGetAuditTrailBodySchema>;

export const ExternalGetTransitQuerySchema = z.object({
  name: z.string().optional(),
  type: z.enum(['All', 'Departure', 'Arrival']).optional(),
  MRN: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

export type ExternalGetTransitQuery = z.infer<typeof ExternalGetTransitQuerySchema>;

export const externalContract = c.router({
  authenticate: {
    method: 'POST',
    path: '/external/authenticate',
    body: z.object({
      email: z.string(),
      password: z.string(),
    }),
    responses: {
      200: z.object({
        token: z.string(),
      }),
    },
  },
  getUser: {
    method: 'GET',
    path: '/external/user',
    responses: {
      200: z.object({
        data: ExternalGetUserResponseSchema,
      }),
    },
  },
  getTemplates: {
    method: 'GET',
    path: '/external/templates',
    responses: {
      200: z.object({
        data: z.array(ExternalGetTemplatesResponseSchema),
      }),
    },
  },
  getCustomers: {
    method: 'GET',
    path: '/external/customers',
    responses: {
      200: z.object({
        data: z.array(ExternalGetCustomersResponseSchema),
      }),
    },
  },
  getBatches: {
    method: 'POST',
    path: '/external/batches',
    body: ExternalGetBatchesBodySchema,
    responses: {
      200: z.object({
        data: z.array(ExternalGetBatchesResponseSchema),
      }),
    },
  },
  sendBatch: {
    method: 'POST',
    path: '/external/send-batch',
    body: z.object({
      batch: z.string(),
      sendPost: z.boolean().optional(),
    }),
    responses: {
      200: z.object({
        message: z.string(),
      }),
    },
  },
  customsSync: {
    method: 'POST',
    path: '/external/customs-sync',
    body: z.object({
      batch: z.string(),
    }),
    responses: {
      200: z.object({
        message: z.string(),
      }),
    },
  },
  uploadBatch: {
    method: 'POST',
    path: '/external/upload-batch',
    contentType: 'multipart/form-data',
    body: z.object({
      customer: z.string(),
      name: z.string(),
      template: z.string(),
      hvSplittingCustomer: z.string().optional(),
      hvSplittingTemplate: z.string().optional(),
      h7ExcludedCustomer: z.string().optional(),
      h7ExcludedTemplate: z.string().optional(),
    }),
    responses: {
      200: z.object({
        message: z.string(),
      }),
    },
  },
  updateBatch: {
    method: 'POST',
    path: '/external/update-batch',
    contentType: 'multipart/form-data',
    body: z.object({
      batch: z.string(),
    }),
    responses: {
      200: z.object({
        data: z.string(),
      }),
    },
  },
  getDeclarations: {
    method: 'POST',
    path: '/external/declarations',
    body: ExternalGetDeclarationsBodySchema,
    responses: {
      200: z.object({
        data: z.array(z.any()),
      }),
    },
  },
  getDeclarationLines: {
    method: 'POST',
    path: '/external/declaration-lines',
    body: z.object({
      LRN: z.string(),
    }),
    responses: {
      200: z.object({
        data: z.array(z.any()),
      }),
    },
  },
  getBatchReport: {
    method: 'POST',
    path: '/external/batch-report',
    body: z.object({
      batch: z.string(),
    }),
    responses: {
      200: z.any(),
    },
  },
  getAuditTrail: {
    method: 'POST',
    path: '/external/audit-trail',
    body: ExternalGetAuditTrailBodySchema,
    responses: {
      200: z.object({
        data: z.array(z.any()),
      }),
    },
  },
  getTransit: {
    method: 'GET',
    path: '/external/transit',
    query: ExternalGetTransitQuerySchema,
    responses: {
      200: z.object({
        data: z.array(z.any()),
      }),
    },
  },
});
